import { useMemo } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { WifiOff } from 'lucide-react-native';
import { MatchHub } from '../../../components/match/MatchHub';
import { MatchHubSkeleton } from '../../../components/match/MatchHub/MatchHubSkeleton';
import { useMatchHubData } from '../../../src/match/hooks/useMatchHubData';
import { useTheme } from '../../../src/theme/useTheme';
import { getMatchHubStyles } from '../../../components/match/MatchHub/MatchHub.styles';

const MATCH_HUB_ERROR_COPY = {
  TITLE: "Couldn't load match",
  SUBTITLE: 'Check your connection and try again.',
  BACK: 'Go back',
} as const;

export default function MatchHubRoute() {
  const router = useRouter();
  const theme = useTheme();
  const layoutStyles = useMemo(() => getMatchHubStyles(theme), [theme]);
  const { gameId, returnTo } = useLocalSearchParams<{ gameId: string; returnTo?: string }>();

  const id = typeof gameId === 'string' ? gameId : '';
  const { data, isLoading, isError } = useMatchHubData(id);
  const returnToPath = typeof returnTo === 'string' ? decodeURIComponent(returnTo) : undefined;
  const safeTarget =
    returnToPath != null &&
    returnToPath.length > 0 &&
    !returnToPath.includes('/arena')
      ? returnToPath
      : '/app/gameResults';
  const handleBack = () => {
    router.replace(safeTarget);
  };

  if (isError) {
    return (
      <SafeAreaView style={[layoutStyles.flex, layoutStyles.bodyBg]} edges={['top', 'left', 'right']}>
        <View style={layoutStyles.errorContainer}>
          <View style={layoutStyles.errorIconWrap}>
            <WifiOff size={48} color={theme.gray[400]} />
          </View>
          <Text style={layoutStyles.errorTitle}>{MATCH_HUB_ERROR_COPY.TITLE}</Text>
          <Text style={layoutStyles.errorSubtitle}>{MATCH_HUB_ERROR_COPY.SUBTITLE}</Text>
          <Pressable style={layoutStyles.errorButton} onPress={handleBack}>
            <Text style={layoutStyles.errorButtonLabel}>{MATCH_HUB_ERROR_COPY.BACK}</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[layoutStyles.flex, layoutStyles.bodyBg]} edges={['top', 'left', 'right']}>
      {isLoading || data === null ? (
        <MatchHubSkeleton />
      ) : (
        <MatchHub data={data} onBack={handleBack} />
      )}
    </SafeAreaView>
  );
}
